import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { History, LoaderCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import * as Query from '../../../api/gql/query'
import { describeScheduleError } from '../lib/errors'
import type { ScheduleActivityLogEntry } from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  scheduleId: string
  activityId?: string
  onFocusActivity?: (id: string) => void
}

const PAGE_SIZE = 50
type T = (key: string, opts?: any) => string

const fmt = (v: any) => (v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v))

/** Renders one log entry's `meta` blob into a human-readable line. Spec B §9.9. */
export const describeMeta = (e: ScheduleActivityLogEntry, t: T): string => {
  const m = e.meta ?? {}
  if (Array.isArray(m.changes)) return m.changes.map((c: any) => `${c.field}: ${fmt(c.old)} → ${fmt(c.new)}`).join('; ')
  if (e.action === 'SCHEDULE_IMPORTED') return `${m.sourceTitle ?? ''} · ${m.activityCount} / ${m.dependencyCount} · ${t('Schedule_import_warning_code')}: ${m.warningCount}`
  if (e.action === 'ACTIVITY_MOVED') return `${m.from?.parentId ?? '—'}[${m.from?.index}] → ${m.to?.parentId ?? '—'}[${m.to?.index}]`
  if (e.action.startsWith('DEPENDENCY_')) return `${m.predecessorName ?? m.predecessorId} ${m.type}${m.lagDays ? (m.lagDays > 0 ? `+${m.lagDays}d` : `${m.lagDays}d`) : ''}`
  if (e.action.startsWith('BASELINE_') || e.action === 'CALENDAR_UPDATED') return m.name ?? ''
  if (e.action === 'ACTIVITY_CREATED' || e.action === 'ACTIVITY_DELETED') return `${m.wbsCode ?? ''} ${m.name ?? ''}`.trim()
  return ''
}

/** Paged audit trail for one schedule, optionally filtered to a single activity. Spec B §9.9. */
const ActivityLogDialog: React.FC<Props> = ({ open, onOpenChange, scheduleId, activityId, onFocusActivity }) => {
  const { t } = useTranslation()
  const [loadingMore, setLoadingMore] = useState(false)
  const { data, loading, fetchMore } = useQuery(Query.SCHEDULE_ACTIVITY_LOG, {
    variables: { scheduleId, activityId, take: PAGE_SIZE, skip: 0 },
    skip: !open,
    fetchPolicy: 'network-only',
  })

  const entries: ScheduleActivityLogEntry[] = data?.scheduleActivityLog ?? []
  const filteredName = useMemo(() => entries.find((e) => e.activityId === activityId)?.activityName, [entries, activityId])

  // Review fix (parked minor): `entries.length % PAGE_SIZE === 0` reads "no more" only when the
  // total isn't an exact multiple of the page size — on an exact multiple it kept offering "Load
  // more" forever. Track whether the *last fetched page itself* came back short instead; reset
  // whenever the log's scope changes (schedule or activity filter).
  const [noMore, setNoMore] = useState(false)
  const prevCountRef = useRef(0)
  useEffect(() => { prevCountRef.current = 0; setNoMore(false) }, [scheduleId, activityId])
  useEffect(() => {
    if (!data?.scheduleActivityLog) return
    const total = data.scheduleActivityLog.length
    const pageLen = total - prevCountRef.current
    prevCountRef.current = total
    if (pageLen < PAGE_SIZE) setNoMore(true)
  }, [data])
  const hasMore = entries.length > 0 && !noMore

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      await fetchMore({
        variables: { skip: entries.length },
        updateQuery: (prev: { scheduleActivityLog: ScheduleActivityLogEntry[] }, { fetchMoreResult }: { fetchMoreResult?: { scheduleActivityLog: ScheduleActivityLogEntry[] } }) => {
          if (!fetchMoreResult) return prev
          return { scheduleActivityLog: [...prev.scheduleActivityLog, ...fetchMoreResult.scheduleActivityLog] }
        },
      })
    } catch (e: any) {
      toastr.error(t('Common_error'), describeScheduleError(e.message, t))
    } finally { setLoadingMore(false) }
  }

  const focusRow = (id: string) => { onFocusActivity?.(id); onOpenChange(false) }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <History className="h-5 w-5 text-primary" />
            {t('Schedule_activity_log')}
            {activityId && <Badge variant="secondary" className="font-normal">{filteredName ?? t('Schedule_log_filtered_activity')}</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground sticky top-0 bg-background">
              <tr>
                <th className="py-1.5 px-2">{t('Schedule_log_time')}</th>
                <th className="py-1.5 px-2">{t('Schedule_log_user')}</th>
                <th className="py-1.5 px-2">{t('Schedule_log_action')}</th>
                <th className="py-1.5 px-2">{t('Schedule_log_activity')}</th>
                <th className="py-1.5 px-2">{t('Schedule_log_details')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t align-top">
                  <td className="py-2 px-2 whitespace-nowrap text-muted-foreground tabular-nums">{new Date(e.createdAt).toLocaleString(t('date_locale') || 'en-US')}</td>
                  <td className="py-2 px-2 whitespace-nowrap">{e.user?.name ?? '—'}</td>
                  <td className="py-2 px-2 whitespace-nowrap">{t(`Schedule_log_${e.action}`)}</td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {e.activityId ? (
                      <Button variant="link" className="h-auto p-0" onClick={() => focusRow(e.activityId!)}>{e.activityName ?? e.activityId}</Button>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-2 text-muted-foreground">{describeMeta(e, t)}</td>
                </tr>
              ))}
              {loading && entries.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center"><LoaderCircle className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('Schedule_log_empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="flex justify-center pt-1">
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Schedule_log_load_more')}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-end pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ActivityLogDialog
